<script lang="ts">
	import { Breakpoints, DateFormats } from '@tools';

	let { from = $bindable(new Date()) }: { from: Date } = $props();
	const current = new Date();
	current.setHours(0, 0, 0, 0);

	const clickYear = (year: number) => {
		from = new Date(year, year === current.getFullYear() ? current.getMonth() : 0);
	};

	const isMonthDisabled = (date: Date) => {
		return date.getFullYear() === current.getFullYear() && date.getMonth() < current.getMonth();
	};

	const daysData = $derived.by(() => {
		const days = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();

		const daysToAddFromPreviousMonth = from.getDay() === 0 ? 6 : Math.max(from.getDay() - 1, 0);
		const totalSlots = daysToAddFromPreviousMonth + days;

		return { inCurrentMonth: days, totalSlots: Math.ceil(totalSlots / 7) * 7 };
	});

	const getDayOfMonth = (day: number) => {
		let startingWeekday = new Date(from.getFullYear(), from.getMonth(), 1).getDay() - 1;
		if (startingWeekday === -1) startingWeekday = 6;
		return day - startingWeekday + 1;
	};

	const monthValueString = (date: Date) => {
		return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
	};
</script>

<section>
	{#if Breakpoints.md}
		<div class="years">
			{#each Array(3) as _, i}
				{@const year = current.getFullYear() + i}
				<button
					class="button:outline no-icon"
					aria-pressed={from.getFullYear() === year}
					onclick={() => {
						clickYear(year);
					}}>{year}</button
				>
			{/each}
		</div>

		<div class="months">
			{#each Array(12) as _, month}
				{@const date = new Date(from.getFullYear(), month)}
				<button
					onclick={() => {
						from = date;
					}}
					class="button:outline no-icon"
					aria-pressed={from.getMonth() === month}
					disabled={isMonthDisabled(date)}
					>{date.toLocaleString(DateFormats.Locale, {
						month: 'long'
					})}</button
				>
			{/each}
		</div>
	{:else}
		{@const max = new Date(current.getFullYear() + 3, -1)}
		<input
			class="button:outline no-icon"
			type="month"
			min={monthValueString(current)}
			max={monthValueString(max)}
			required
			value={monthValueString(from)}
			onchange={(e) => {
				if (!e.currentTarget.value) {
					e.preventDefault();
					return;
				}
				from = new Date(e.currentTarget.value);
			}}
		/>
	{/if}

	<div class="days">
		{#each Array(7) as _, day}
			<!-- because this year starts on a monday -->
			{@const date = new Date(2024, 0, day + 1)}
			<div class="weekday">
				{date.toLocaleString(DateFormats.Locale, {
					weekday: 'narrow'
				})}
			</div>
		{/each}
		{#each Array(daysData.totalSlots) as _, day}
			{@const dayOfMonth = getDayOfMonth(day)}
			{#if dayOfMonth <= 0 || dayOfMonth > daysData.inCurrentMonth}
				<button class="day button:outline no-icon" disabled aria-hidden="true"></button>
			{:else}
				{@const date = new Date(from.getFullYear(), from.getMonth(), dayOfMonth)}
				<button
					class="day button:outline no-icon"
					onclick={() => {
						from = date;
					}}
					aria-pressed={from.getDate() === date.getDate()}
					disabled={date < current}>{dayOfMonth}</button
				>
			{/if}
		{/each}
	</div>
</section>

<style lang="scss">
	section {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: $gap6;
		padding: $gap6 var(--gutter);

		@include md {
			flex-direction: row;
			justify-content: space-between;
			gap: var(--gap);
			padding: $gap7 var(--gutter);
		}

		@include lg {
			padding-left: calc(cols(12, 1, true, true) + (var(--gutter) - var(--gap)));
			padding-right: calc(cols(12, 1, true, true) + (var(--gutter) - var(--gap)));
		}
	}

	.years {
		display: flex;
		flex-direction: column;
		gap: $gap3;
		align-items: stretch;
		text-align: center;

		@include lg {
			min-width: cols(10, 1);
		}
	}

	.months {
		flex: 1 1 0;
		gap: $gap3;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		max-width: cols(12, 7);

		@include lg {
			max-width: cols(10, 5);
		}
	}

	.days {
		display: grid;
		grid-template-columns: repeat(7, max-content);
		grid-auto-rows: max-content;
		gap: $gap3;
		justify-items: center;
		text-align: center;
		justify-content: center;
	}

	button,
	input {
		justify-content: center;
		text-transform: capitalize;
	}

	input {
		width: 100%;
		max-width: 25em;
	}

	.day {
		height: 2.067em;
		width: 2.067em;
		padding: 0 0;
	}
</style>
